import React from 'react'
import type { ReactNode } from 'react'

import BaseButton from '@components/common/BaseButton'

interface TimeEntryDialogFooterProps {
  mode?: 'normal' | 'review'
  disabled?: boolean
  interactionLocked?: boolean
  saveLoading?: boolean
  saveAndReleaseLoading?: boolean
  onCancel: () => void
  onSave: () => void
  onSaveAndRelease: () => void
  referenceActions?: ReactNode
  showCancel?: boolean
  showSaveActions?: boolean
}

interface TimeEntryDialogSaveActionsProps {
  disabled?: boolean
  saveLoading?: boolean
  saveAndReleaseLoading?: boolean
  onSave: () => void
  onSaveAndRelease: () => void
  // The full-page editor shows Save first in its header (MIRA-4296); the
  // dialog footer keeps Save as the rightmost primary action.
  order?: 'footer' | 'header'
}

export const TimeEntryDialogSaveActions = ({
  disabled = false,
  saveLoading = false,
  saveAndReleaseLoading = false,
  onSave,
  onSaveAndRelease,
  order = 'footer',
}: TimeEntryDialogSaveActionsProps) => {
  const saveAndRelease = (
    <BaseButton
      key="save-and-release"
      fillMode="solid"
      themeColor="base"
      className="h-8 min-h-0 w-auto overflow-hidden border !border-[#DADADA] !bg-white px-3 py-[5px] text-[14px] text-sm font-normal leading-5 text-[#333] hover:!bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-45"
      disabled={disabled}
      loading={saveAndReleaseLoading}
      loader={{ loaderSize: 'xsmall' }}
      preserveWidthOnLoading
      onClick={onSaveAndRelease}
    >
      Save & Release
    </BaseButton>
  )
  const save = (
    <BaseButton
      key="save"
      fillMode="solid"
      themeColor="dark"
      className="h-8 min-h-0 overflow-hidden border !border-[#DADADA] !bg-[#333] px-3 py-[5px] text-[14px] text-sm font-normal leading-5 !text-white hover:!bg-black disabled:cursor-not-allowed disabled:opacity-45"
      disabled={disabled}
      loading={saveLoading}
      loader={{ loaderSize: 'xsmall' }}
      preserveWidthOnLoading
      onClick={onSave}
    >
      Save
    </BaseButton>
  )

  return (
    <div className="flex h-8 items-center gap-2">
      {order === 'header' ? [save, saveAndRelease] : [saveAndRelease, save]}
    </div>
  )
}

const TimeEntryDialogFooter = ({
  mode = 'normal',
  disabled = false,
  interactionLocked = false,
  saveLoading = false,
  saveAndReleaseLoading = false,
  onCancel,
  onSave,
  onSaveAndRelease,
  referenceActions,
  showCancel = true,
  showSaveActions = true,
}: TimeEntryDialogFooterProps) => (
  <div className="box-border flex w-full shrink-0 items-center justify-between gap-2 rounded-b-xl border-t border-[#d9d9d9] bg-white px-6 py-3">
    <div className="flex h-8 min-w-[69px] items-center">{referenceActions}</div>
    <div className="flex h-8 items-center gap-2">
      {showCancel && mode !== 'review' && (
        <BaseButton
          fillMode="solid"
          themeColor="base"
          className="h-8 min-h-0 w-auto border !border-[#DADADA] !bg-white px-3 py-[5px] text-[14px] text-sm font-normal leading-5 text-[#333] hover:!bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-45"
          disabled={interactionLocked}
          onClick={onCancel}
        >
          Cancel
        </BaseButton>
      )}

      {showSaveActions && (
        <TimeEntryDialogSaveActions
          disabled={disabled}
          saveLoading={saveLoading}
          saveAndReleaseLoading={saveAndReleaseLoading}
          onSave={onSave}
          onSaveAndRelease={onSaveAndRelease}
        />
      )}
    </div>
  </div>
)

export default TimeEntryDialogFooter
